import { FlexCol } from '@/components/common/Flex';
import { LinkText, Paragraph, Text } from '@/components/common/Typography';
import { toDollars } from '@/util/currency';
import { ListItem, UnorderedList } from '@chakra-ui/react';
import { VacationGoal } from '@common/goals';
import { getGoalBaseConfig } from '@common/react/goals';
import {
    VacationContinent,
    VacationCountry,
    VacationExpenseLevel,
    VacationPeople,
} from '@common/types';
import { dt, isPastDate } from '@common/util/dateTime';
import { getRoundedUp } from '@common/util/math';
import pluralize from '@common/util/pluralize';
import { capitalize } from '@common/util/strings';
import { isEmpty, isNumber } from '@common/util/types';
import isEqual from 'lodash.isequal';
import GoalStatementLinkText from '../GoalStatementLinkText';
import {
    getIOrWe,
    getRecommendedRiskScoreUsingTargetDate,
    getTargetDateFromYearMonth,
    getYearsOldStr,
} from '../helpers';
import { GoalConfig, GoalData } from './types';

const peopleInfoByValue: Record<
    VacationPeople,
    { label: string; number: number }
> = {
    ME: { label: 'Just me', number: 1 },
    ME_PLUS_1: { label: 'Me + 1 person', number: 2 },
    ME_PLUS_2: { label: 'Me + 2 people', number: 3 },
    ME_PLUS_3: { label: 'Me + 3 people', number: 4 },
    ME_PLUS_4: { label: 'Me + 4 people', number: 5 },
    ME_PLUS_5: { label: 'Me + 5 people', number: 6 },
    ME_PLUS_6: { label: 'Me + 6 people', number: 7 },
    ME_PLUS_7: { label: 'Me + 7 people', number: 8 },
    ME_PLUS_8: { label: 'Me + 8 people', number: 9 },
} as const;

// This is used for all continents other than North America
const longDistanceFlightCostPerPersonByExpenseLevel: Record<
    VacationExpenseLevel,
    number
> = {
    BUDGET: 1457_00,
    MID_RANGE: 1655_00,
    LUXURY: 2897_00,
};

const continentInfoByValue: Record<
    VacationContinent,
    {
        countries: string;
        description: string;
        costPerDayPerPersonByExpenseLevel: Record<VacationExpenseLevel, number>;
        flightCostPerPersonByExpenseLevel: Record<VacationExpenseLevel, number>;
    }
> = {
    Africa: {
        countries: 'South Africa, Tanzania, Ethiopia, Mozambique...',
        description: `Seek out the animals of the great plains, wonder at the immensity of the ancient Pyramids, or trek your way to the summit of wild Mt.Kilimanjaro.`,
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 28_00,
            MID_RANGE: 66_00,
            LUXURY: 146_00,
        },
        flightCostPerPersonByExpenseLevel:
            longDistanceFlightCostPerPersonByExpenseLevel,
    },
    Asia: {
        countries: 'Thailand, Japan, Hong Kong, Bhutan...',
        description: `Wander around ancient temples of Japan, climb the great wall, stare out at a Himalayan kingdom and cap it off with the spice-filled smells of a 2 am night market feast.`,
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 38_00,
            MID_RANGE: 83_00,
            LUXURY: 200_00,
        },
        flightCostPerPersonByExpenseLevel:
            longDistanceFlightCostPerPersonByExpenseLevel,
    },
    Caribbean: {
        countries: 'Cuba, Jamaica, Dominican Repulic, Martinique...',
        description: `Don’t worry... about a thing. Bask in the sun as you drive around singing to Bob Marley, maybe get caught in the rain while sipping pina coladas.`,
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 96_00,
            MID_RANGE: 226_00,
            LUXURY: 488_00,
        },
        flightCostPerPersonByExpenseLevel:
            longDistanceFlightCostPerPersonByExpenseLevel,
    },
    'Central America': {
        countries: 'Costa Rica, Panama, Mexico, Nicaragua...',
        description: `Surf through warm pacific waters, adventure through lush rainforests, or maybe brush up on your Spanish to eat your way through the culinary delights of Mexico city.`,
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 28_00,
            MID_RANGE: 72_00,
            LUXURY: 187_00,
        },
        flightCostPerPersonByExpenseLevel:
            longDistanceFlightCostPerPersonByExpenseLevel,
    },
    Europe: {
        countries: 'France, Italy, Iceland, Croatia...',
        description: `Taste perfectly al-dente pastas or maybe, pick up some flamenco dancing, or stroll through the galleries with ancient art capturing hundreds of years of human creativity.`,
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 51_00,
            MID_RANGE: 121_00,
            LUXURY: 284_00,
        },
        flightCostPerPersonByExpenseLevel:
            longDistanceFlightCostPerPersonByExpenseLevel,
    },
    'Middle East': {
        countries: 'UAE, Israel, Turkey, Lebanon...',
        description: `Experience luxurious, modern cities in the middle of the desert, leisurely walk through ancient cities, and admire intricate temples towering above the streets.`,
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 52_00,
            MID_RANGE: 121_00,
            LUXURY: 276_00,
        },
        flightCostPerPersonByExpenseLevel:
            longDistanceFlightCostPerPersonByExpenseLevel,
    },
    'North America': {
        countries: 'US, Canada',
        description: `Stay up all night in the iconically busy city of NY, road trip to national parks larger than most countries in the world, and be welcomed by the friendly multicultural locals of Canada, eh?`,
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 65_00,
            MID_RANGE: 178_00,
            LUXURY: 529_00,
        },
        flightCostPerPersonByExpenseLevel: {
            BUDGET: 339_00,
            MID_RANGE: 385_00,
            LUXURY: 674_00,
        },
    },
    Pacific: {
        countries: 'Australia, New Zealand,  Fiji, Tahiti',
        description: `Indescribably picturesque waters of snorkeling paradises, the immense wilderness of a campervan-friendly, LOTR-inspiring world.`,
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 76_00,
            MID_RANGE: 165_00,
            LUXURY: 355_00,
        },
        flightCostPerPersonByExpenseLevel:
            longDistanceFlightCostPerPersonByExpenseLevel,
    },
    'South America': {
        countries: 'Brazil, Argentina, Peru, Colombia...',
        description: `Maybe you’ll wander the awe-inspiring ruins of Machu Picchu, feel the power of the Iguazu falls, or dance along to Rio’s carnival.`,
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 30_00,
            MID_RANGE: 72_00,
            LUXURY: 187_00,
        },
        flightCostPerPersonByExpenseLevel:
            longDistanceFlightCostPerPersonByExpenseLevel,
    },
} as const;

const countryInfoByValue: Record<
    VacationCountry,
    {
        continent: VacationContinent;
        description: string;
        costPerDayPerPersonByExpenseLevel: Record<VacationExpenseLevel, number>;
    }
> = {
    Afghanistan: {
        continent: 'Asia',
        description:
            "From the stunning Hindu Kush mountain range to the ancient ruins of the Silk Road, there's something for everyone.",
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 25_00,
            MID_RANGE: 62_00,
            LUXURY: 157_00,
        },
    },
    Albania: {
        continent: 'Europe',
        description:
            'From the turquoise waters of the Albanian Riviera to the rugged peaks of the Accursed Mountains, Albania is a hidden gem.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 23_00,
            MID_RANGE: 56_00,
            LUXURY: 125_00,
        },
    },
    Algeria: {
        continent: 'Africa',
        description:
            'Climb the dramatic dunes of the Sahara or the majestic peaks of the Atlas Mountains.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 25_00,
            MID_RANGE: 62_00,
            LUXURY: 157_00,
        },
    },
    Andorra: {
        continent: 'Europe',
        description:
            'Explore charming mountain villages, soak in thermal spas, and indulge in world-class skiing and snowboarding.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 70_00,
            MID_RANGE: 164_00,
            LUXURY: 339_00,
        },
    },
    Angola: {
        continent: 'Africa',
        description:
            'Explore Angola’s rich history and culture through its museums, art galleries, and music scene.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 70_00,
            MID_RANGE: 164_00,
            LUXURY: 339_00,
        },
    },
    'Antigua and Barbuda': {
        continent: 'Caribbean',
        description:
            'Enjoy a paradise-like experience with pristine beaches, crystal-clear waters, and year-round sunshine.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 136_00,
            MID_RANGE: 330_00,
            LUXURY: 758_00,
        },
    },
    Argentina: {
        continent: 'South America',
        description:
            'Stroll down the romantic streets of Buenos Aires and share a toast to the rolling vineyards of Mendoza.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 26_00,
            MID_RANGE: 43_00,
            LUXURY: 117_00,
        },
    },
    Armenia: {
        continent: 'Europe',
        description:
            'Explore historic monasteries and churches, sample delicious cuisine and wine, and take in the breathtaking views of rugged mountains.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 12_00,
            MID_RANGE: 28_00,
            LUXURY: 57_00,
        },
    },
    Aruba: {
        continent: 'Caribbean',
        description:
            'Aruba is a paradise for beach lovers, with its pristine white sand beaches and crystal-clear turquoise waters.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 130_00,
            MID_RANGE: 304_00,
            LUXURY: 640_00,
        },
    },
    Australia: {
        continent: 'Pacific',
        description:
            "G'day! From the Great Barrier Reef to the Outback, Australia offers diverse landscapes and natural wonders.",
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 57_00,
            MID_RANGE: 147_00,
            LUXURY: 395_00,
        },
    },
    Austria: {
        continent: 'Europe',
        description:
            'Explore historic cities like Vienna and Salzburg, ski in the majestic Alps, or simply savor delightful cuisine.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 59_00,
            MID_RANGE: 146_00,
            LUXURY: 363_00,
        },
    },
    Azerbaijan: {
        continent: 'Asia',
        description:
            'Explore the ancient walled city of Baku, hike in the stunning Caucasus Mountains, or relax in healing mineral springs.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 37_00,
            MID_RANGE: 87_00,
            LUXURY: 165_00,
        },
    },
    Bahamas: {
        continent: 'Caribbean',
        description:
            'Enjoy a tropical paradise with crystal-clear turquoise waters, pristine white sand beaches, and warm sunshine year-round.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 101_00,
            MID_RANGE: 234_00,
            LUXURY: 490_00,
        },
    },
    Bahrain: {
        continent: 'Middle East',
        description:
            'Explore historic landmarks like the Bahrain Fort, indulge in world-class shopping and dining, or unwind in luxurious spas and resorts.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 68_00,
            MID_RANGE: 150_00,
            LUXURY: 280_00,
        },
    },
    Bangladesh: {
        continent: 'Asia',
        description:
            "Explore ancient archaeological sites, cruise the country's picturesque rivers, or indulge in its flavorful cuisine and warm hospitality.",
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 22_00,
            MID_RANGE: 51_00,
            LUXURY: 99_00,
        },
    },
    Barbados: {
        continent: 'Caribbean',
        description:
            'Relax and enjoy this Caribbean gem with pristine beaches, turquoise waters, and a vibrant cultural scene.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 140_00,
            MID_RANGE: 351_00,
            LUXURY: 861_00,
        },
    },
    Belarus: {
        continent: 'Europe',
        description:
            "Explore ancient castles, enjoy world-class museums, or hike in the country's stunning national parks.",
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 34_00,
            MID_RANGE: 82_00,
            LUXURY: 192_00,
        },
    },
    Belgium: {
        continent: 'Europe',
        description:
            'Visit historic cities like Brussels and Bruges and indulge in delicious chocolate and waffles.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 57_00,
            MID_RANGE: 142_00,
            LUXURY: 333_00,
        },
    },
    Belize: {
        continent: 'Central America',
        description:
            "Explore ancient Maya ruins, snorkel or dive in the country's beautiful barrier reef, or relax and unwind in a hammock.",
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 37_00,
            MID_RANGE: 98_00,
            LUXURY: 269_00,
        },
    },
    Benin: {
        continent: 'Africa',
        description:
            "Visit ancient voodoo temples, see wildlife in national parks, or soak up the country's vibrant music and art scene.",
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 26_00,
            MID_RANGE: 57_00,
            LUXURY: 110_00,
        },
    },
    Bermuda: {
        continent: 'Caribbean',
        description:
            'Enjoy this beautiful island paradise with pink sand beaches, turquoise waters, and a rich cultural history.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 124_00,
            MID_RANGE: 287_00,
            LUXURY: 565_00,
        },
    },
    Bhutan: {
        continent: 'Asia',
        description:
            "Explore ancient monasteries and fortresses, trek through the Himalayas, or soak up the country's serene and welcoming atmosphere.",
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 37_00,
            MID_RANGE: 87_00,
            LUXURY: 165_00,
        },
    },
    Bolivia: {
        continent: 'South America',
        description:
            'Experience rich cultural heritage and diverse landscapes, from the towering peaks of the Andes to the vast expanse of the Amazon rainforest.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 20_00,
            MID_RANGE: 49_00,
            LUXURY: 123_00,
        },
    },
    'Bosnia and Herzegovina': {
        continent: 'Europe',
        description:
            'Explore historic cities like Sarajevo and Mostar or marvel at the natural wonder of the Plitvice Lakes National Park.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 27_00,
            MID_RANGE: 68_00,
            LUXURY: 167_00,
        },
    },
    Botswana: {
        continent: 'Africa',
        description:
            'Marvel at a land of incredible natural beauty and diverse wildlife, from the shimmering salt pans of the Kalahari Desert to the lush Okavango Delta.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 26_00,
            MID_RANGE: 57_00,
            LUXURY: 112_00,
        },
    },
    Brazil: {
        continent: 'South America',
        description:
            'Olá! (That’s hello in Brazilian Portuguese) Experience the rhythms of samba in Rio de Janeiro to the natural wonders of the Amazon rainforest.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 29_00,
            MID_RANGE: 77_00,
            LUXURY: 219_00,
        },
    },
    'British Virgin Islands': {
        continent: 'Caribbean',
        description:
            'Explore the vibrant coral reefs, sail around the islands, or simply relax and soak up the sun.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 132_00,
            MID_RANGE: 309_00,
            LUXURY: 631_00,
        },
    },
    Brunei: {
        continent: 'Asia',
        description:
            'Explore the capital city of Bandar Seri Begawan, or trek through the lush jungles of Ulu Temburong National Park.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 32_00,
            MID_RANGE: 75_00,
            LUXURY: 158_00,
        },
    },
    Bulgaria: {
        continent: 'Europe',
        description:
            'Explore the ancient ruins of Plovdiv and Sofia to the stunning natural beauty of the Black Sea coast and the Rila Mountains.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 26_00,
            MID_RANGE: 63_00,
            LUXURY: 149_00,
        },
    },
    'Burkina Faso': {
        continent: 'Africa',
        description:
            'Experience vibrant local communities, discover ancient rock formations, and enjoy the warm and welcoming culture.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 12_00,
            MID_RANGE: 26_00,
            LUXURY: 48_00,
        },
    },
    Burundi: {
        continent: 'Africa',
        description:
            'Discover national parks teeming with wildlife, hike through scenic mountains and forests, and enjoy the vibrant local music and art scenes.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 26_00,
            MID_RANGE: 57_00,
            LUXURY: 110_00,
        },
    },
    Cambodia: {
        continent: 'Asia',
        description:
            "Cambodia's rich history and culture, with ancient temples such as Angkor Wat, brings visitors from all over the world.",
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 20_00,
            MID_RANGE: 57_00,
            LUXURY: 183_00,
        },
    },
    Cameroon: {
        continent: 'Africa',
        description:
            "Cameroon's diverse array of landscapes, from lush rainforests to savannahs and mountains, makes it a nature lover's paradise.",
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 26_00,
            MID_RANGE: 57_00,
            LUXURY: 110_00,
        },
    },
    Canada: {
        continent: 'North America',
        description:
            'Canada’s vast wilderness, friendly locals, and cosmopolitan cities make it a top destination for nature lovers and urban explorers alike.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 57_00,
            MID_RANGE: 159_00,
            LUXURY: 484_00,
        },
    },
    'Cabo Verde': {
        continent: 'Africa',
        description:
            'Cabo Verde, an archipelago of 10 volcanic islands off the coast of West Africa, boasts stunning beaches and crystal-clear waters.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 53_00,
            MID_RANGE: 119_00,
            LUXURY: 229_00,
        },
    },
    'Cayman Islands': {
        continent: 'Caribbean',
        description:
            'Enjoy this idyllic Caribbean getaway with white sand beaches, crystal-clear waters, and outdoor activities like snorkeling, diving, and paddle boarding.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 125_00,
            MID_RANGE: 303_00,
            LUXURY: 682_00,
        },
    },
    'Central African Republic': {
        continent: 'Africa',
        description:
            'A place of incredible natural beauty, from dense tropical forests and sprawling savannas to winding rivers and vast grasslands.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 26_00,
            MID_RANGE: 57_00,
            LUXURY: 110_00,
        },
    },
    Chad: {
        continent: 'Africa',
        description:
            "Explore Zakouma National Park for wildlife enthusiasts, the Ennedi Plateau for breathtaking rock formations, and the beautiful capital city of N'Djamena.",
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 26_00,
            MID_RANGE: 57_00,
            LUXURY: 110_00,
        },
    },
    Chile: {
        continent: 'South America',
        description:
            'Journey from the towering Andes Mountains to the rugged Patagonian glaciers.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 44_00,
            MID_RANGE: 118_00,
            LUXURY: 336_00,
        },
    },
    China: {
        continent: 'Asia',
        description:
            "Nǐ hǎo! (That’s hello in Chinese) Visit the Great Wall and the Forbidden City in Beijing, the Terracotta Warriors in Xi'an, and the futuristic skyline of Shanghai.",
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 29_00,
            MID_RANGE: 75_00,
            LUXURY: 187_00,
        },
    },
    Colombia: {
        continent: 'South America',
        description:
            'Explore Caribbean-like beaches, lush jungles, and indulge in rich coffee culture.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 12_00,
            MID_RANGE: 35_00,
            LUXURY: 113_00,
        },
    },
    Comoros: {
        continent: 'Africa',
        description:
            'Comoros is a small island nation boasting stunning beaches, lush forests, and a unique blend of African, Arabic, and French cultures.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 26_00,
            MID_RANGE: 57_00,
            LUXURY: 110_00,
        },
    },
    Congo: {
        continent: 'Africa',
        description:
            'From hiking in the Virunga Mountains to visiting the wildlife in Kahuzi-Biega National Park, the country has much to offer.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 53_00,
            MID_RANGE: 130_00,
            LUXURY: 305_00,
        },
    },
    'Costa Rica': {
        continent: 'Central America',
        description:
            'From exploring volcanoes and national parks to soaking in hot springs and surfing, Costa Rica offers something for everyone.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 35_00,
            MID_RANGE: 90_00,
            LUXURY: 227_00,
        },
    },
    "Côte d'Ivoire": {
        continent: 'Africa',
        description:
            'Soak in the vibrant markets of Abidjan or the peaceful beaches of Grand Bassam.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 48_00,
            MID_RANGE: 123_00,
            LUXURY: 310_00,
        },
    },
    Croatia: {
        continent: 'Europe',
        description:
            'Explore the charming streets of Dubrovnik and discover the cascading Plitvice Lakes.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 39_00,
            MID_RANGE: 95_00,
            LUXURY: 225_00,
        },
    },
    Cuba: {
        continent: 'Caribbean',
        description:
            "From the old streets of Havana to the stunning beaches of Varadero, there's no shortage of music, art, architecture, and cuisine to enjoy.",
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 14_00,
            MID_RANGE: 39_00,
            LUXURY: 124_00,
        },
    },
    Curacao: {
        continent: 'Caribbean',
        description:
            "Whether you're exploring historic Willemstad or snorkeling at the Blue Bay Beach, Curacao is a must-visit destination.",
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 135_00,
            MID_RANGE: 310_00,
            LUXURY: 614_00,
        },
    },
    Cyprus: {
        continent: 'Europe',
        description:
            'Cyprus is a beautiful Mediterranean island with a rich history, stunning beaches, picturesque villages, and delicious cuisine.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 44_00,
            MID_RANGE: 102_00,
            LUXURY: 214_00,
        },
    },
    'Czech Republic': {
        continent: 'Europe',
        description:
            "Visit Prague's historic old town, explore the picturesque Bohemian countryside, and indulge in delicious Czech cuisine.",
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 44_00,
            MID_RANGE: 108_00,
            LUXURY: 268_00,
        },
    },
    Denmark: {
        continent: 'Europe',
        description:
            'Denmark’s cozy cafes, charming architecture, and relaxed atmosphere make it an ideal place to unwind and enjoy the simple pleasures in life.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 78_00,
            MID_RANGE: 180_00,
            LUXURY: 419_00,
        },
    },
    Djibouti: {
        continent: 'Africa',
        description:
            'Discover the otherworldly landscapes of Lake Assal, and the opportunity to swim with whale sharks in the Gulf of Tadjoura.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 26_00,
            MID_RANGE: 57_00,
            LUXURY: 110_00,
        },
    },
    Dominica: {
        continent: 'Caribbean',
        description:
            'With its lush rainforests, volcanic peaks and stunning waterfalls, Dominica is a paradise for nature lovers.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 83_00,
            MID_RANGE: 191_00,
            LUXURY: 366_00,
        },
    },
    'Dominican Republic': {
        continent: 'Caribbean',
        description:
            'Explore the colorful streets of Santo Domingo, swim in the turquoise waters of Punta Cana, or hike through the lush mountains of Jarabacoa.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 53_00,
            MID_RANGE: 135_00,
            LUXURY: 349_00,
        },
    },
    Ecuador: {
        continent: 'South America',
        description:
            'A few must-see highlights include the Amazon basin, the Otavalo market, the Avenue of Volcanoes, and the historic center of Quito.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 22_00,
            MID_RANGE: 60_00,
            LUXURY: 158_00,
        },
    },
    Egypt: {
        continent: 'Africa',
        description:
            'From the Pyramids of Giza to the Nile River, Egypt’s ancient history and culture offer a glimpse into the wonders of the past.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 15_00,
            MID_RANGE: 37_00,
            LUXURY: 101_00,
        },
    },
    'El Salvador': {
        continent: 'Central America',
        description:
            'Explore ancient Mayan ruins, relax on beautiful beaches, hike through lush forests, and experience vibrant cities with delicious cuisine and friendly locals.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 35_00,
            MID_RANGE: 90_00,
            LUXURY: 227_00,
        },
    },
    'Equatorial Guinea': {
        continent: 'Africa',
        description:
            'Enjoy pristine beaches, lush rainforests, and unique wildlife, as well as experience the vibrant local music and cuisine.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 13_00,
            MID_RANGE: 35_00,
            LUXURY: 103_00,
        },
    },
    Eritrea: {
        continent: 'Africa',
        description:
            'Eritrea is a hidden gem located in the Horn of Africa with stunning coral reefs, ancient ruins, and colorful markets.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 26_00,
            MID_RANGE: 57_00,
            LUXURY: 110_00,
        },
    },
    Estonia: {
        continent: 'Europe',
        description:
            'From the charming medieval old town of Tallinn to the untouched wilderness of Soomaa National Park, Estonia has something for everyone.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 41_00,
            MID_RANGE: 101_00,
            LUXURY: 246_00,
        },
    },
    Ethiopia: {
        continent: 'Africa',
        description:
            'Explore ancient historical sites like the rock-hewn churches of Lalibela and the castles of Gondar, and marvel at the Simien Mountains and the Blue Nile Falls.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 21_00,
            MID_RANGE: 57_00,
            LUXURY: 174_00,
        },
    },
    Fiji: {
        continent: 'Pacific',
        description:
            'With its crystal-clear waters, white sand beaches, and warm hospitality, Fiji is the ultimate tropical paradise.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 69_00,
            MID_RANGE: 163_00,
            LUXURY: 359_00,
        },
    },
    Finland: {
        continent: 'Europe',
        description:
            'Finland is a land of breathtaking natural beauty, from its countless lakes and pristine forests to the stunning Northern Lights.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 64_00,
            MID_RANGE: 156_00,
            LUXURY: 343_00,
        },
    },
    France: {
        continent: 'Europe',
        description:
            'Bonjour! (That’s hello in French) Must-see landmarks include the Eiffel Tower, the Palace of Versailles, the Louvre Museum, and the Mont-Saint-Michel.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 80_00,
            MID_RANGE: 232_00,
            LUXURY: 761_00,
        },
    },
    Gabon: {
        continent: 'Africa',
        description:
            'Gabon has more than 10 national parks and nature reserves that are home to a diverse range of wildlife, including gorillas, elephants, and hippos.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 26_00,
            MID_RANGE: 57_00,
            LUXURY: 110_00,
        },
    },
    Gambia: {
        continent: 'Africa',
        description:
            'Explore the colonial architecture of Banjul, cruise on the Gambia River, and witness traditional ceremonies and music performances.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 26_00,
            MID_RANGE: 57_00,
            LUXURY: 110_00,
        },
    },
    Georgia: {
        continent: 'Europe',
        description:
            'From the snow-capped peaks of the Caucasus Mountains to the vibrant streets of Tbilisi, there is no shortage of things to see and do.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 12_00,
            MID_RANGE: 32_00,
            LUXURY: 95_00,
        },
    },
    Germany: {
        continent: 'Europe',
        description:
            'Hallo! (That’s hello in German) Germany’s world-class museums, vibrant cities, and techno nightclubs offer something for everyone.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 61_00,
            MID_RANGE: 151_00,
            LUXURY: 365_00,
        },
    },
    Ghana: {
        continent: 'Africa',
        description:
            'Visit the castles of Cape Coast or the bustling markets of Kumasi.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 12_00,
            MID_RANGE: 30_00,
            LUXURY: 90_00,
        },
    },
    Greece: {
        continent: 'Europe',
        description:
            'Explore the Acropolis in Athens, soak up the sun on the island of Santorini, or indulge in delicious Mediterranean cuisine.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 61_00,
            MID_RANGE: 153_00,
            LUXURY: 391_00,
        },
    },
    Grenada: {
        continent: 'Caribbean',
        description:
            "Discover the colorful capital of St. George's, snorkel in the crystal-clear waters of Grand Anse Beach, and sample the island's famous spices at a local market.",
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 116_00,
            MID_RANGE: 277_00,
            LUXURY: 614_00,
        },
    },
    Guadeloupe: {
        continent: 'Caribbean',
        description:
            'From hiking to water sports, music festivals to local markets, there is something for everyone to enjoy in Guadeloupe.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 90_00,
            MID_RANGE: 226_00,
            LUXURY: 558_00,
        },
    },
    Guatemala: {
        continent: 'Central America',
        description:
            'Experience rich Mayan history, ancient ruins, colonial architecture, colorful markets, stunning lakes, and majestic volcanoes.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 18_00,
            MID_RANGE: 46_00,
            LUXURY: 115_00,
        },
    },
    Guinea: {
        continent: 'Africa',
        description:
            'Guinea offers a rich cultural heritage and a diverse landscape, from the lively capital city of Conakry to the stunning beaches and mountains of the Fouta Djallon region.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 18_00,
            MID_RANGE: 46_00,
            LUXURY: 115_00,
        },
    },
    'Guinea-Bissau': {
        continent: 'Africa',
        description:
            'Explore the Bijagos Archipelago and see wildlife, enjoy beautiful beaches and experience the local culture.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 18_00,
            MID_RANGE: 46_00,
            LUXURY: 115_00,
        },
    },
    Guyana: {
        continent: 'Africa',
        description:
            'From the misty Kaieteur Falls to the historic capital city of Georgetown, there is much to explore and discover in this hidden gem.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 48_00,
            MID_RANGE: 123_00,
            LUXURY: 310_00,
        },
    },
    Haiti: {
        continent: 'Caribbean',
        description:
            'Explore the colorful streets of Port-au-Prince, trek through the lush mountains, and experience the vibrant music and dance scene.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 26_00,
            MID_RANGE: 35_00,
            LUXURY: 56_00,
        },
    },
    Honduras: {
        continent: 'Central America',
        description:
            "From the Mayan ruins of Copan to the pristine beaches of Roatan, there's something for everyone to enjoy.",
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 21_00,
            MID_RANGE: 54_00,
            LUXURY: 137_00,
        },
    },
    'Hong Kong': {
        continent: 'Asia',
        description:
            'Must-see attractions include Victoria Peak for stunning city views, the Big Buddha on Lantau Island, and the vibrant street markets for a taste of local life.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 64_00,
            MID_RANGE: 161_00,
            LUXURY: 404_00,
        },
    },
    Hungary: {
        continent: 'Europe',
        description:
            "Experience beautiful architecture, thermal baths, and historic sites such as the Buda Castle and Fisherman's Bastion in Budapest.",
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 30_00,
            MID_RANGE: 78_00,
            LUXURY: 202_00,
        },
    },
    Iceland: {
        continent: 'Europe',
        description:
            'Iceland’s otherworldly landscapes, including glaciers, geysers, and hot springs, make it a must-see destination for nature lovers and adventure seekers.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 80_00,
            MID_RANGE: 181_00,
            LUXURY: 426_00,
        },
    },
    India: {
        continent: 'Asia',
        description:
            'See the iconic Taj Mahal, visit the colorful markets of Rajasthan, and marvel at the majestic Himalayas.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 14_00,
            MID_RANGE: 37_00,
            LUXURY: 104_00,
        },
    },
    Indonesia: {
        continent: 'Asia',
        description:
            'Relax on the the beaches of Bali, explore the temples of Java, wander the jungles of Sumatra, and see the volcanoes of Lombok.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 20_00,
            MID_RANGE: 56_00,
            LUXURY: 170_00,
        },
    },
    Iran: {
        continent: 'Middle East',
        description:
            'From the ancient ruins of Persepolis to the colorful mosques of Isfahan, immerse yourself in beauty and complexity.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 19_00,
            MID_RANGE: 54_00,
            LUXURY: 149_00,
        },
    },
    Iraq: {
        continent: 'Middle East',
        description:
            'Visit the ancient city of Babylon, see the ruins of Hatra, discover the stunning Ziggurat of Ur, and marvel at the picturesque landscapes of the Kurdistan region.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 19_00,
            MID_RANGE: 54_00,
            LUXURY: 149_00,
        },
    },
    Ireland: {
        continent: 'Europe',
        description:
            'From the rugged coastline of the Wild Atlantic Way to the historic streets of Dublin, there is something for everyone to discover and enjoy.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 55_00,
            MID_RANGE: 137_00,
            LUXURY: 333_00,
        },
    },
    Israel: {
        continent: 'Middle East',
        description:
            'Explore ancient cities like Jerusalem and Nazareth, soak up the sun in Tel Aviv and Eilat, and hike through the Negev and the Judean Hills.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 47_00,
            MID_RANGE: 113_00,
            LUXURY: 266_00,
        },
    },
    Italy: {
        continent: 'Europe',
        description:
            'Ciao! (That’s hello in Italian). Explore the ancient ruins of Rome, see the canals of Venice, and relax with a view of the rolling hills of Tuscany.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 66_00,
            MID_RANGE: 165_00,
            LUXURY: 417_00,
        },
    },
    Jamaica: {
        continent: 'Caribbean',
        description:
            'Explore historic sites like the Bob Marley Museum, swim in crystal-clear waters, sample the local cuisine, and enjoy the vibrant, island culture.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 48_00,
            MID_RANGE: 123_00,
            LUXURY: 310_00,
        },
    },
    Japan: {
        continent: 'Asia',
        description:
            'Konnichiwa! (That’s hello in Japanese) Explore stunning shrines and temples, indulge in delicious cuisine, and shop for the latest tech. ',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 47_00,
            MID_RANGE: 119_00,
            LUXURY: 304_00,
        },
    },
    Jordan: {
        continent: 'Middle East',
        description:
            'Explore the rock-carved city of Petra, float in the Dead Sea, and marvel at the red sandstone landscapes of Wadi Rum.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 59_00,
            MID_RANGE: 143_00,
            LUXURY: 324_00,
        },
    },
    Kazakhstan: {
        continent: 'Asia',
        description:
            'Explore the modern metropolis of Astana, the ancient Silk Road city of Almaty, and the stunning Altai Mountains and Charyn Canyon.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 22_00,
            MID_RANGE: 51_00,
            LUXURY: 99_00,
        },
    },
    Kenya: {
        continent: 'Africa',
        description:
            'Immerse in the animal kingdom on a safari and witness the natural spectacle of the Great Migration.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 51_00,
            MID_RANGE: 122_00,
            LUXURY: 257_00,
        },
    },
    Kiribati: {
        continent: 'Africa',
        description:
            "Enjoy an idyllic paradise of coral atolls, turquoise waters, and pristine beaches with some of the world's most spectacular underwater scenery for divers.",
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 12_00,
            MID_RANGE: 27_00,
            LUXURY: 82_00,
        },
    },
    "Korea (Democratic People's Republic of)": {
        continent: 'Asia',
        description:
            'Visit landmarks like the Juche Tower and Kumsusan Palace of the Sun, and see stunning natural attractions such as the Mount Kumgang and Ullim Waterfall.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 37_00,
            MID_RANGE: 96_00,
            LUXURY: 245_00,
        },
    },
    'South Korea': {
        continent: 'Asia',
        description:
            'Explore bustling cities like Seoul, discover tranquil temples, marvel at picturesque mountains, and enjoy scenic coastlines,',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 47_00,
            MID_RANGE: 119_00,
            LUXURY: 304_00,
        },
    },
    Kuwait: {
        continent: 'Asia',
        description:
            'From the stunning modern architecture of Kuwait City to the historic markets, mosques, and museums, Kuwait has something for everyone.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 184_00,
            MID_RANGE: 288_00,
            LUXURY: 522_00,
        },
    },
    Kyrgyzstan: {
        continent: 'Asia',
        description:
            'Kyrgyzstan offers breathtaking mountain landscapes and a chance to immerse in nomadic culture',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 12_00,
            MID_RANGE: 27_00,
            LUXURY: 82_00,
        },
    },
    "Lao People's Democratic Republic": {
        continent: 'Asia',
        description:
            'Explore stunning waterfalls, trek in lush forests, or simply relax in a peaceful atmosphere.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 22_00,
            MID_RANGE: 51_00,
            LUXURY: 99_00,
        },
    },
    Latvia: {
        continent: 'Europe',
        description:
            'Explore sandy beaches, dense forests, castles and charming medieval old towns.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 46_00,
            MID_RANGE: 113_00,
            LUXURY: 277_00,
        },
    },
    Lebanon: {
        continent: 'Middle East',
        description:
            'Lebanon is rich in history and culture, with ancient ruins like the Temple of Bacchus and the Jeita Grotto, and vibrant cities like Beirut.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 12_00,
            MID_RANGE: 27_00,
            LUXURY: 82_00,
        },
    },
    Lesotho: {
        continent: 'Africa',
        description:
            'Explore vast highlands, picturesque valleys, and traditional villages.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 26_00,
            MID_RANGE: 60_00,
            LUXURY: 113_00,
        },
    },
    Liberia: {
        continent: 'Africa',
        description:
            'Liberia is a country of stunning natural beauty, with lush rainforests, pristine beaches, and abundant wildlife.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 12_00,
            MID_RANGE: 27_00,
            LUXURY: 82_00,
        },
    },
    Libya: {
        continent: 'Africa',
        description:
            'Discover the ancient Roman ruins of Leptis Magna or dive into the stunning coastline of the Mediterranean.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 12_00,
            MID_RANGE: 27_00,
            LUXURY: 82_00,
        },
    },
    Liechtenstein: {
        continent: 'Europe',
        description:
            'Experience breathtaking Alpine scenery, picturesque villages, and charming castles.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 103_00,
            MID_RANGE: 229_00,
            LUXURY: 435_00,
        },
    },
    Lithuania: {
        continent: 'Europe',
        description:
            'Explore medieval castles and cobblestone streets, relax on sandy beaches, or immerse yourself in the vibrant art and culture scene.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 37_00,
            MID_RANGE: 91_00,
            LUXURY: 209_00,
        },
    },
    Luxembourg: {
        continent: 'Europe',
        description:
            'Explore historic castles, charming villages, scenic countryside, and a thriving modern city all in one trip.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 80_00,
            MID_RANGE: 184_00,
            LUXURY: 365_00,
        },
    },
    'North Macedonia': {
        continent: 'Europe',
        description:
            'Experience stunning mountain ranges, serene lakes, and historic landmarks such as the ancient city of Ohrid and the Kale Fortress in Skopje.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 12_00,
            MID_RANGE: 27_00,
            LUXURY: 82_00,
        },
    },
    Macau: {
        continent: 'Asia',
        description:
            "Visit iconic attractions such as the Macau Tower, the Ruins of St. Paul's, and the famous casinos on the Cotai Strip.",
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 118_00,
            MID_RANGE: 136_00,
            LUXURY: 177_00,
        },
    },
    Madagascar: {
        continent: 'Africa',
        description:
            'Explore the lush rainforests, stunning beaches, and discover the endemic lemurs.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 35_00,
            MID_RANGE: 82_00,
            LUXURY: 174_00,
        },
    },
    Malawi: {
        continent: 'Africa',
        description:
            'Visit Lake Malawi, the third largest lake in Africa, and explore national parks teeming with wildlife such as elephants, lions, and hippos.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 26_00,
            MID_RANGE: 57_00,
            LUXURY: 110_00,
        },
    },
    Malaysia: {
        continent: 'Asia',
        description:
            'Experience the majestic towering skyscrapers of Kuala Lumpur to the pristine beaches of Langkawi.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 26_00,
            MID_RANGE: 74_00,
            LUXURY: 234_00,
        },
    },
    Maldives: {
        continent: 'Asia',
        description:
            'Relax in this island paradise known for its clear turquoise waters, white sandy beaches, and coral reefs teeming with marine life.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 113_00,
            MID_RANGE: 280_00,
            LUXURY: 668_00,
        },
    },
    Mali: {
        continent: 'Africa',
        description:
            'See stunning architecture such as the Great Mosque of Djenné, and breathtaking natural wonders such as the Sahara Desert and the Niger River.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 18_00,
            MID_RANGE: 28_00,
            LUXURY: 50_00,
        },
    },
    Malta: {
        continent: 'Europe',
        description:
            'With a rich history dating back to prehistoric times, explore ancient temples, stunning beaches, charming cities and towns.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 184_00,
            MID_RANGE: 288_00,
            LUXURY: 522_00,
        },
    },
    Martinique: {
        continent: 'Caribbean',
        description:
            'Explore historic sites like Fort-de-France, hike through the tropical landscape, or simply relax on the sandy shores while sipping on a refreshing rum cocktail.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 66_00,
            MID_RANGE: 150_00,
            LUXURY: 291_00,
        },
    },
    'Marshall Islands': {
        continent: 'Pacific',
        description:
            'Experience the unique culture, explore historic World War II sites, and enjoy a variety of water-based activities including diving, snorkeling, and fishing.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 184_00,
            MID_RANGE: 288_00,
            LUXURY: 522_00,
        },
    },
    Mauritania: {
        continent: 'Africa',
        description:
            "Discover historic libraries and stunning mosque architecture, as well as breathtaking natural wonders like the Banc d'Arguin National Park.",
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 39_00,
            MID_RANGE: 50_00,
            LUXURY: 70_00,
        },
    },
    Mauritius: {
        continent: 'Africa',
        description:
            'Relax and unwind in a stunning paradise of white sandy beaches, crystal clear waters, and palm trees swaying in the breeze.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 26_00,
            MID_RANGE: 57_00,
            LUXURY: 110_00,
        },
    },
    Mexico: {
        continent: 'North America',
        description:
            'Indulge in rich culture, delicious food, crystalline beaches, and warm hospitality in Mexico.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 39_00,
            MID_RANGE: 110_00,
            LUXURY: 342_00,
        },
    },
    Micronesia: {
        continent: 'Pacific',
        description:
            'Enjoy crystal clear waters, pristine beaches, and a vibrant marine life to explore through activities like snorkeling and scuba diving.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 116_00,
            MID_RANGE: 263_00,
            LUXURY: 504_00,
        },
    },
    Morocco: {
        continent: 'Africa',
        description:
            'Immerse yourself in the exotic sights, sounds, and flavors of Morocco, from the bustling souks of Marrakech to the serene sands of the Sahara.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 19_00,
            MID_RANGE: 48_00,
            LUXURY: 116_00,
        },
    },
    Moldova: {
        continent: 'Europe',
        description:
            'From the stunning Orheiul Vechi monastery to the world-renowned Mileștii Mici wine cellars, Moldova offers a unique blend of history, nature, and wine tasting experiences.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 12_00,
            MID_RANGE: 27_00,
            LUXURY: 82_00,
        },
    },
    Monaco: {
        continent: 'Europe',
        description:
            'Visit the famous Monte Carlo Casino, take a stroll through the old town, and enjoy the breathtaking views of the Mediterranean Sea.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 92_00,
            MID_RANGE: 211_00,
            LUXURY: 413_00,
        },
    },
    Mongolia: {
        continent: 'Asia',
        description:
            'Explore the Gobi desert, the rugged Altai Mountains, ancient monasteries and modern cities.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 39_00,
            MID_RANGE: 51_00,
            LUXURY: 76_00,
        },
    },
    Montenegro: {
        continent: 'Europe',
        description:
            'Must-see attractions include the fortified town of Kotor, the stunning Durmitor National Park, and the beautiful beaches of Budva.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 43_00,
            MID_RANGE: 105_00,
            LUXURY: 242_00,
        },
    },
    Mozambique: {
        continent: 'Africa',
        description:
            "Explore the vibrant culture and history of cities like Maputo, or embark on a safari adventure to see some of Africa's iconic wildlife.",
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 26_00,
            MID_RANGE: 57_00,
            LUXURY: 110_00,
        },
    },
    Myanmar: {
        continent: 'Asia',
        description:
            'Discover the ancient city of Bagan, the iconic Shwedagon Pagoda in Yangon, the picturesque Inle Lake and the stunning temples of Mandalay.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 39_00,
            MID_RANGE: 51_00,
            LUXURY: 76_00,
        },
    },
    Namibia: {
        continent: 'Africa',
        description:
            'Explore the towering dunes of Sossusvlei, track cheetahs in the Central Highlands, and admire the dramatic scenery of Fish River Canyon, among many other natural wonders.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 34_00,
            MID_RANGE: 71_00,
            LUXURY: 163_00,
        },
    },
    Nauru: {
        continent: 'Pacific',
        description:
            'A tiny island nation located in the Pacific Ocean with beautiful beaches and crystal clear waters perfect for diving and snorkeling.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 13_00,
            MID_RANGE: 35_00,
            LUXURY: 103_00,
        },
    },
    Nepal: {
        continent: 'Asia',
        description:
            "Be sure to see the iconic Himalayas, the bustling capital city of Kathmandu, and the country's many ancient temples and monasteries.",
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 13_00,
            MID_RANGE: 35_00,
            LUXURY: 103_00,
        },
    },
    Netherlands: {
        continent: 'Europe',
        description:
            'Explore the picturesque canals of Amsterdam, the historic windmills, world-class museums, and charming countryside dotted with colorful houses and cycling paths.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 70_00,
            MID_RANGE: 174_00,
            LUXURY: 420_00,
        },
    },
    'New Zealand': {
        continent: 'Pacific',
        description:
            'New Zealand offers some of the most stunning natural landscapes in the world, from snow-capped mountains to pristine beaches and fjords.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 49_00,
            MID_RANGE: 132_00,
            LUXURY: 386_00,
        },
    },
    Nicaragua: {
        continent: 'Central America',
        description:
            'Experience adventure activities such as surfing, zip-lining, and volcano hiking, or relax in charming towns and soak up the laid-back vibe.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 19_00,
            MID_RANGE: 51_00,
            LUXURY: 147_00,
        },
    },
    Niger: {
        continent: 'Africa',
        description:
            'Visit the Sahara desert, the River Niger, and the W National Park, where you can see elephants, lions, cheetahs, and giraffes.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 13_00,
            MID_RANGE: 35_00,
            LUXURY: 103_00,
        },
    },
    Nigeria: {
        continent: 'Africa',
        description:
            'Dance along the bustling streets of Lagos or visit the ancient city of Benin.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 13_00,
            MID_RANGE: 35_00,
            LUXURY: 103_00,
        },
    },
    Norway: {
        continent: 'Europe',
        description:
            'From the Northern Lights to the Midnight Sun, Norway is a must-visit destination for nature lovers and adventurers alike.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 49_00,
            MID_RANGE: 112_00,
            LUXURY: 250_00,
        },
    },
    Oman: {
        continent: 'Middle East',
        description:
            'Enjoy stunning beaches, dramatic mountains, ancient forts, traditional markets, and warm hospitality.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 108_00,
            MID_RANGE: 227_00,
            LUXURY: 425_00,
        },
    },
    Pakistan: {
        continent: 'Asia',
        description:
            'Highlights include the stunning natural beauty of the Karakoram Range, the vibrant cultural scene of Lahore, and the historic architecture of Islamabad.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 25_00,
            MID_RANGE: 62_00,
            LUXURY: 157_00,
        },
    },
    Palau: {
        continent: 'Pacific',
        description:
            "Palau, a tiny archipelago in the Pacific Ocean, is a diver's paradise with crystal clear waters, vibrant coral reefs, and diverse marine life.",
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 13_00,
            MID_RANGE: 35_00,
            LUXURY: 103_00,
        },
    },
    Panama: {
        continent: 'Central America',
        description:
            "From the modern Panama City skyline to the stunning beaches, islands, and rainforests, there's something for everyone to enjoy.",
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 29_00,
            MID_RANGE: 74_00,
            LUXURY: 184_00,
        },
    },
    'Papua New Guinea': {
        continent: 'Pacific',
        description:
            'Explore ancient cultures, trek through rugged jungles, dive among colorful coral reefs, and experience the warm hospitality of the local people.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 87_00,
            MID_RANGE: 214_00,
            LUXURY: 510_00,
        },
    },
    Paraguay: {
        continent: 'South America',
        description:
            'Behold the rugged beauty of Table Mountain or the stunning savannas of Kruger National Park.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 42_00,
            MID_RANGE: 74_00,
            LUXURY: 130_00,
        },
    },
    Peru: {
        continent: 'South America',
        description:
            'Discover the ancient Inca civilization from the iconic Machu Picchu to the mysterious Nazca Lines.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 20_00,
            MID_RANGE: 56_00,
            LUXURY: 168_00,
        },
    },
    Philippines: {
        continent: 'Asia',
        description:
            'A beautiful archipelago made up of over 7,000 islands with crystal-clear waters, white sand beaches, vibrant coral reefs, stunning waterfalls, and lush jungles.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 22_00,
            MID_RANGE: 59_00,
            LUXURY: 152_00,
        },
    },
    Poland: {
        continent: 'Europe',
        description:
            'Charming medieval cities like Krakow, stunning natural landscapes like the Tatra Mountains, and delicious cuisine like pierogi and kielbasa.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 21_00,
            MID_RANGE: 56_00,
            LUXURY: 150_00,
        },
    },
    Portugal: {
        continent: 'Europe',
        description:
            'Enjoy exploring historic cities, sample delicious cuisine, and experience the warm hospitality.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 55_00,
            MID_RANGE: 136_00,
            LUXURY: 318_00,
        },
    },
    'Puerto Rico': {
        continent: 'Caribbean',
        description:
            'Explore the colorful streets of Old San Juan, soak up the sun on pristine beaches, and hike through lush rainforests.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 92_00,
            MID_RANGE: 204_00,
            LUXURY: 425_00,
        },
    },
    Qatar: {
        continent: 'Middle East',
        description:
            'Explore the bustling souqs, marvel at the modern skyscrapers, or relax on the beautiful beaches.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 90_00,
            MID_RANGE: 191_00,
            LUXURY: 387_00,
        },
    },
    Romania: {
        continent: 'Europe',
        description:
            'From the dramatic Carpathian Mountains to the charming villages of Transylvania and the vibrant capital of Bucharest, Romania has something for everyone.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 36_00,
            MID_RANGE: 77_00,
            LUXURY: 164_00,
        },
    },
    Russia: {
        continent: 'Europe',
        description:
            "See the colorful onion domes of St. Basil's Cathedral in Moscow, the breathtaking beauty of Lake Baikal and the stunning palaces of St. Petersburg.",
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 27_00,
            MID_RANGE: 74_00,
            LUXURY: 215_00,
        },
    },
    Rwanda: {
        continent: 'Africa',
        description:
            'Known as the land of a thousand hills, offering stunning natural beauty and wildlife, including the endangered mountain gorillas in the Volcanoes National Park.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 26_00,
            MID_RANGE: 57_00,
            LUXURY: 110_00,
        },
    },
    'Saint Kitts and Nevis': {
        continent: 'Caribbean',
        description:
            "From exploring the colonial architecture of Basseterre to hiking the volcanic slopes of Mount Liamuiga, there's plenty to see and do in this tropical paradise.",
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 119_00,
            MID_RANGE: 290_00,
            LUXURY: 672_00,
        },
    },
    'Saint Lucia': {
        continent: 'Caribbean',
        description:
            "Explore the world's only drive-in volcano, hike to breathtaking waterfalls, and indulge in the island's unique culture and cuisine.",
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 83_00,
            MID_RANGE: 198_00,
            LUXURY: 427_00,
        },
    },
    'Saint Vincent and the Grenadines': {
        continent: 'Caribbean',
        description:
            'Enjoy an idyllic Caribbean experience with pristine beaches, crystal-clear waters, lush rainforests, and unique cultural sites.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 68_00,
            MID_RANGE: 154_00,
            LUXURY: 294_00,
        },
    },
    Samoa: {
        continent: 'Pacific',
        description:
            'From the pristine beaches and crystal-clear waters to the lush rainforests and cascading waterfalls, Samoa offers a stunning natural beauty and rich cultural experiences.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 32_00,
            MID_RANGE: 75_00,
            LUXURY: 158_00,
        },
    },
    'Sao Tome and Principe': {
        continent: 'Africa',
        description:
            "Explore the islands' colonial history, sample the local cuisine, and discover unique species of flora and fauna found nowhere else in the world.",
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 34_00,
            MID_RANGE: 71_00,
            LUXURY: 163_00,
        },
    },
    'Saudi Arabia': {
        continent: 'Middle East',
        description:
            'Explore the bustling cities of Riyadh and Jeddah, visit the breathtaking rock formations of Wadi Rum, or take a spiritual journey to the holy cities of Mecca and Medina.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 44_00,
            MID_RANGE: 108_00,
            LUXURY: 268_00,
        },
    },
    Senegal: {
        continent: 'Africa',
        description:
            'Behold the colorful streets of Dakar or relax at the stunning beaches of Saly.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 53_00,
            MID_RANGE: 130_00,
            LUXURY: 305_00,
        },
    },
    Serbia: {
        continent: 'Europe',
        description:
            'Experience rich cultural heritage and stunning natural beauty, including the majestic Danube River and the towering peaks of the Dinaric Alps.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 15_00,
            MID_RANGE: 36_00,
            LUXURY: 92_00,
        },
    },
    Seychelles: {
        continent: 'Africa',
        description:
            'Seychelles is a paradise destination, featuring stunning beaches with turquoise waters, lush tropical forests, and unique wildlife.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 84_00,
            MID_RANGE: 193_00,
            LUXURY: 366_00,
        },
    },
    'Sierra Leone': {
        continent: 'Africa',
        description:
            'Take a boat ride to the Banana Islands, explore the charming colonial architecture of Freetown, and enjoy the warmth and hospitality of the Sierra Leonean people.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 26_00,
            MID_RANGE: 57_00,
            LUXURY: 110_00,
        },
    },
    Singapore: {
        continent: 'Asia',
        description:
            'From the iconic Marina Bay Sands to the lush Gardens by the Bay and the bustling hawker centers, there is always something new to discover in this dynamic city.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 50_00,
            MID_RANGE: 133_00,
            LUXURY: 370_00,
        },
    },
    Slovakia: {
        continent: 'Europe',
        description:
            'Slovakia is a country full of medieval castles, charming small towns, and breathtaking natural beauty.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 55_00,
            MID_RANGE: 125_00,
            LUXURY: 277_00,
        },
    },
    Slovenia: {
        continent: 'Europe',
        description:
            'From the picturesque Lake Bled to the charming capital of Ljubljana, there is something for every type of traveler in this beautiful country.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 40_00,
            MID_RANGE: 94_00,
            LUXURY: 198_00,
        },
    },
    'Solomon Islands': {
        continent: 'Pacific',
        description:
            'From diving among the colorful coral reefs to trekking through dense rainforests, discover a wealth of natural wonders and experience the warm hospitality of the local people.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 70_00,
            MID_RANGE: 160_00,
            LUXURY: 308_00,
        },
    },
    Somalia: {
        continent: 'Africa',
        description:
            'Explore the historic sites of Mogadishu, the beautiful beaches of Berbera, and the wildlife of the Somali desert.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 26_00,
            MID_RANGE: 57_00,
            LUXURY: 110_00,
        },
    },
    'South Africa': {
        continent: 'Africa',
        description:
            'Explore the rugged coastline of the Western Cape and experience the vibrant culture and history of Johannesburg and Cape Town.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 33_00,
            MID_RANGE: 84_00,
            LUXURY: 223_00,
        },
    },
    'South Sudan': {
        continent: 'Africa',
        description:
            "From the expansive savannahs of Boma National Park to the picturesque town of Juba, there's something for everyone to discover in this vibrant nation.",
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 12_00,
            MID_RANGE: 27_00,
            LUXURY: 82_00,
        },
    },
    Spain: {
        continent: 'Europe',
        description:
            'Spain is a land of rich culture and stunning landscapes, from the sun-soaked beaches of the Mediterranean to the towering peaks of the Pyrenees.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 59_00,
            MID_RANGE: 149_00,
            LUXURY: 386_00,
        },
    },
    'Sri Lanka': {
        continent: 'Asia',
        description:
            'From ancient temples and ruins to lush tea plantations, stunning beaches, and exotic wildlife, this tropical paradise offers something for everyone.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 12_00,
            MID_RANGE: 21_00,
            LUXURY: 68_00,
        },
    },
    Sudan: {
        continent: 'Africa',
        description:
            'Sudan is a country steeped in history, with numerous ancient ruins and monuments to explore, including the pyramids of Meroë and the ancient city of Naqa.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 12_00,
            MID_RANGE: 27_00,
            LUXURY: 82_00,
        },
    },
    Suriname: {
        continent: 'Africa',
        description:
            'From the bustling markets of Paramaribo to the pristine rainforests of the interior, visitors can experience a unique and unforgettable journey off the beaten path.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 12_00,
            MID_RANGE: 27_00,
            LUXURY: 82_00,
        },
    },
    Eswatini: {
        continent: 'Africa',
        description:
            'Explore traditional Swazi culture, see wildlife in its natural habitat, and take part in exciting outdoor activities like hiking and zip-lining.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 55_00,
            MID_RANGE: 125_00,
            LUXURY: 239_00,
        },
    },
    Sweden: {
        continent: 'Europe',
        description:
            'Sweden is a country full of natural beauty, from the Northern Lights to the archipelagos along the coast.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 54_00,
            MID_RANGE: 132_00,
            LUXURY: 309_00,
        },
    },
    Switzerland: {
        continent: 'Europe',
        description:
            'From skiing in the Swiss Alps to enjoying a relaxing day on Lake Geneva, Switzerland is a destination that has something for everyone.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 109_00,
            MID_RANGE: 263_00,
            LUXURY: 621_00,
        },
    },
    Syria: {
        continent: 'Middle East',
        description:
            'From Crusader castles to jaw-dropping shrines, remote Christian monasteries, bustling souks, and ancient Roman ruins, there are so many outstanding places to visit.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 12_00,
            MID_RANGE: 27_00,
            LUXURY: 82_00,
        },
    },
    Taiwan: {
        continent: 'Asia',
        description:
            'Enjoy bustling night markets to serene hot springs, stunning natural landscapes, and rich cultural heritage.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 39_00,
            MID_RANGE: 106_00,
            LUXURY: 316_00,
        },
    },
    Tahiti: {
        continent: 'Pacific',
        description:
            'Enjoy a range of activities from hiking to water sports, immerse yourself in the Polynesian culture and indulge in delicious French-inspired cuisine.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 98_00,
            MID_RANGE: 183_00,
            LUXURY: 335_00,
        },
    },
    Tajikistan: {
        continent: 'Asia',
        description:
            'Explore ancient Silk Road cities, hike through rugged terrain, and indulge in warm hospitality and delicious local cuisine.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 12_00,
            MID_RANGE: 27_00,
            LUXURY: 82_00,
        },
    },
    Tanzania: {
        continent: 'Africa',
        description:
            'Trek up Mount Kilimanjaro or relax on the pristine beaches of Zanzibar.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 37_00,
            MID_RANGE: 99_00,
            LUXURY: 277_00,
        },
    },
    Thailand: {
        continent: 'Asia',
        description:
            'Explore ancient temples and ruins, visit bustling markets, and soak in the natural beauty of the national parks and islands.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 32_00,
            MID_RANGE: 99_00,
            LUXURY: 356_00,
        },
    },
    Togo: {
        continent: 'Africa',
        description:
            'Enjoy a unique opportunity to explore West African culture with its vibrant markets, traditional festivals, and fascinating Voodoo practices.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 26_00,
            MID_RANGE: 57_00,
            LUXURY: 110_00,
        },
    },
    Tonga: {
        continent: 'Africa',
        description:
            'Explore ancient tombs, watch traditional dance performances, and soak up the laid-back island lifestyle.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 18_00,
            MID_RANGE: 46_00,
            LUXURY: 115_00,
        },
    },
    'Trinidad and Tobago': {
        continent: 'Caribbean',
        description:
            'Experience culture, music, and stunning natural beauty, from the colorful Carnival celebrations to the beautiful beaches and lush rainforests.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 87_00,
            MID_RANGE: 199_00,
            LUXURY: 384_00,
        },
    },
    Tunisia: {
        continent: 'Africa',
        description:
            'Wander the ancient ruins of Carthage or the vibrant markets of Tunis.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 12_00,
            MID_RANGE: 27_00,
            LUXURY: 82_00,
        },
    },
    'Turks and Caicos': {
        continent: 'Caribbean',
        description:
            'Snorkel or dive among colorful reefs, explore uninhabited cays, or simply soak up the sun on the endless stretches of beach.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 132_00,
            MID_RANGE: 307_00,
            LUXURY: 610_00,
        },
    },
    Turkey: {
        continent: 'Europe',
        description:
            'From the ancient ruins of Ephesus to the stunning beaches of Antalya and the magical landscapes of Cappadocia, there is something for everyone to explore and discover.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 12_00,
            MID_RANGE: 27_00,
            LUXURY: 82_00,
        },
    },
    Turkmenistan: {
        continent: 'Africa',
        description:
            'Explore ancient cities, visit beautiful mosques, and marvel at the beauty of the Karakum Desert.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 44_00,
            MID_RANGE: 102_00,
            LUXURY: 214_00,
        },
    },
    Tuvalu: {
        continent: 'Pacific',
        description:
            'Enjoy pristine beaches, crystal-clear waters for swimming and snorkeling, and experience Polynesian culture through traditional music, dance, and handicrafts.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 12_00,
            MID_RANGE: 27_00,
            LUXURY: 82_00,
        },
    },
    Uganda: {
        continent: 'Africa',
        description:
            'Experience a rich diversity of wildlife, like the endangered mountain gorillas, as well as stunning natural landscapes such as the source of the Nile, Murchison Falls, and the Rwenzori Mountains.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 40_00,
            MID_RANGE: 95_00,
            LUXURY: 200_00,
        },
    },
    Ukraine: {
        continent: 'Europe',
        description:
            'From the stunning architecture of Kiev and Lviv to the beautiful Carpathian Mountains and the Black Sea coast, there is something for everyone to discover and enjoy.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 11_00,
            MID_RANGE: 25_00,
            LUXURY: 62_00,
        },
    },
    'United Arab Emirates': {
        continent: 'Middle East',
        description:
            "From the world's tallest building, the Burj Khalifa, to the breathtaking Sheikh Zayed Grand Mosque, there is no shortage of awe-inspiring sights and experiences in this vibrant and dynamic country.",
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 94_00,
            MID_RANGE: 236_00,
            LUXURY: 625_00,
        },
    },
    'United Kingdom': {
        continent: 'Europe',
        description:
            'From the iconic Big Ben and Buckingham Palace in London to the breathtaking natural beauty of the Scottish Highlands and the stunning coastline of Cornwall, there is something for everyone to enjoy.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 67_00,
            MID_RANGE: 171_00,
            LUXURY: 446_00,
        },
    },
    'United States of America': {
        continent: 'North America',
        description:
            'Experience the American dream with its iconic landmarks, bustling cities, and fascinating history.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 98_00,
            MID_RANGE: 264_00,
            LUXURY: 761_00,
        },
    },
    'US Virgin Islands': {
        continent: 'Caribbean',
        description:
            'The US Virgin Islands offer visitors a tropical paradise with stunning beaches, crystal-clear waters, and coral reefs for snorkeling and diving.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 98_00,
            MID_RANGE: 233_00,
            LUXURY: 500_00,
        },
    },
    Uruguay: {
        continent: 'South America',
        description:
            'From the vibrant streets of Montevideo to the breathtaking beaches of Punta del Este, Uruguay offers an unforgettable travel experience for all who visit.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 32_00,
            MID_RANGE: 75_00,
            LUXURY: 158_00,
        },
    },
    Uzbekistan: {
        continent: 'Asia',
        description:
            'Uzbekistan offers a glimpse into the rich history of the Silk Road with its stunning mosques, mausoleums, and minarets.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 12_00,
            MID_RANGE: 27_00,
            LUXURY: 82_00,
        },
    },
    Vanuatu: {
        continent: 'Pacific',
        description:
            'Explore traditional villages, hike to cascading waterfalls, dive with colorful marine life, and relax in idyllic island resorts.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 185_00,
            MID_RANGE: 427_00,
            LUXURY: 848_00,
        },
    },
    Venezuela: {
        continent: 'South America',
        description:
            'Explore the towering Angel Falls, the stunning beaches of Isla Margarita, the vast grasslands of Los Llanos and the colorful cities of Caracas and Mérida.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 53_00,
            MID_RANGE: 135_00,
            LUXURY: 349_00,
        },
    },
    Vietnam: {
        continent: 'Asia',
        description:
            'Discover the stunning Halong Bay and the bustling streets of Hanoi, the majestic temples of Hue, and the picturesque countryside of Sapa.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 21_00,
            MID_RANGE: 59_00,
            LUXURY: 170_00,
        },
    },
    Yemen: {
        continent: 'Asia',
        description:
            'From the ancient city of Sanaa to the stunning Socotra Island, Yemen offers a unique and unforgettable travel experience.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 12_00,
            MID_RANGE: 27_00,
            LUXURY: 82_00,
        },
    },
    Zambia: {
        continent: 'Africa',
        description:
            'Zambia is a treasure trove of natural wonders with its stunning Victoria Falls, diverse wildlife at South Luangwa National Park, and the mighty Zambezi River.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 26_00,
            MID_RANGE: 57_00,
            LUXURY: 110_00,
        },
    },
    Zimbabwe: {
        continent: 'Africa',
        description:
            'From the majestic Victoria Falls to the expansive Hwange National Park, there is no shortage of breathtaking sights to explore in this African country.',
        costPerDayPerPersonByExpenseLevel: {
            BUDGET: 21_00,
            MID_RANGE: 57_00,
            LUXURY: 174_00,
        },
    },
} as const;

const expenseLevelInfoByValue: Record<
    VacationExpenseLevel,
    { label: string; description: string }
> = {
    BUDGET: {
        label: 'Budget',
        description: `Hostels or BnBs are more your speed; your dollars stretch further at discounted museum hours and local food trucks or night markets.`,
    },
    MID_RANGE: {
        label: 'Mid-Range',
        description: `Living like a local through Airbnbs or centrally located hotel chains; your night out may start at a nice restaurant, maybe a local arts show, and you don’t sweat the 2nd (or 4th!) drink.`,
    },
    LUXURY: {
        label: 'Luxury',
        description: `High thread counts, lush bath towels, Michelin-star dining and curated experiences like a private hot tub overlooking a snowy mountain.`,
    },
} as const;

export const VACATION: GoalConfig<VacationGoal> = {
    inputSteps: [
        {
            id: 'days',
            title: 'Ideally, how many days will your vacation be?',
            fieldConfigs: [
                {
                    fieldName: 'days',
                    fieldType: 'integer',
                    fieldValueDefault: 7,
                    min: 1,
                    slider: {
                        min: 1,
                        max: 30,
                        step: 1,
                    },
                    bottomContent: ({ goalData }) => {
                        const { days } = goalData;
                        if (!isNumber(days)) return <></>;
                        const str =
                            days <= 6
                                ? `A change of scenery is the perfect cure for cabin fever.`
                                : days <= 15
                                  ? `Ah, the perfect vacation length to forget your work passwords.`
                                  : `You may need a staycation after your vacation.`;
                        return (
                            <>
                                {/* HACK: This will be replaced as part of PLE-327 */}
                                <Text
                                    fontSize="xl"
                                    ml={days < 10 ? 5 : 8}
                                    mt={'-37px'}
                                >
                                    {pluralize('day', days, false)}
                                </Text>
                                <Paragraph
                                    fontSize="sm"
                                    color="textMuted"
                                    mt={8}
                                    minH={10}
                                >
                                    {str}
                                </Paragraph>
                            </>
                        );
                    },
                },
            ],
        },
        {
            id: 'people',
            title: `How many people will you be traveling with (and paying for)?`,
            fieldConfigs: [
                {
                    fieldName: 'people',
                    fieldType: 'select',
                    fieldOptions: Object.entries(peopleInfoByValue).map(
                        ([value, info]) => ({ value, label: info.label })
                    ),
                    fieldValueDefault: 'ME_PLUS_1',
                },
            ],
        },
        {
            id: 'destination',
            title: `Where do you want to go?`,
            fieldConfigs: [
                {
                    fieldName: 'continent',
                    fieldType: 'select',
                    fieldOptions: Object.entries(continentInfoByValue).map(
                        ([continent]) => ({
                            value: continent,
                            label: continent,
                        })
                    ),
                    fieldValueDefault: 'North America',
                },
                {
                    fieldName: 'country',
                    fieldType: 'select',
                    fieldOptions: ({ goalData }) => [
                        { value: '', label: '' },
                        ...Object.entries(countryInfoByValue)
                            .filter(
                                ([, { continent }]) =>
                                    continent === goalData.continent
                            )
                            .map(([country]) => ({
                                value: country,
                                label: country,
                            })),
                    ],
                    placeholder: 'Country',
                    isOptional: true,
                    bottomContent: ({ goalData }) => {
                        if (goalData.country) {
                            const info = countryInfoByValue[goalData.country];
                            return (
                                <FlexCol mt={4} h={36}>
                                    <Paragraph fontSize="sm" color="textMuted">
                                        {info.description}
                                    </Paragraph>
                                </FlexCol>
                            );
                        } else if (goalData.continent) {
                            const info =
                                continentInfoByValue[goalData.continent];
                            return (
                                <FlexCol mt={4} gap={4} h={36}>
                                    <Paragraph
                                        fontSize="sm"
                                        fontWeight="semibold"
                                        color="textMuted"
                                    >
                                        {info.countries}
                                    </Paragraph>
                                    <Paragraph fontSize="sm" color="textMuted">
                                        {info.description}
                                    </Paragraph>
                                </FlexCol>
                            );
                        }
                        return <></>;
                    },
                },
            ],
        },
        {
            id: 'expenseLevel',
            title: `What type of vacation will this be?`,
            fieldConfigs: [
                {
                    fieldName: 'expenseLevel',
                    fieldType: 'select',
                    fieldOptions: Object.entries(expenseLevelInfoByValue).map(
                        ([value, info]) => ({ value, label: info.label })
                    ),
                    fieldValueDefault: 'MID_RANGE',
                    bottomContent: ({ goalData }) => {
                        if (!goalData.expenseLevel) return <></>;
                        const info =
                            expenseLevelInfoByValue[goalData.expenseLevel];
                        return (
                            <Paragraph
                                fontSize="sm"
                                color="textMuted"
                                mt={4}
                                h={24}
                            >
                                {info.description}
                            </Paragraph>
                        );
                    },
                },
            ],
        },
        {
            id: 'yearMonth',
            title: 'When do you want to go on this vacation?',
            fieldConfigs: [
                {
                    fieldName: 'yearMonth',
                    fieldType: 'yearMonth',
                    bottomContent: ({ goalData, userInfo }) => (
                        <Paragraph fontSize="sm" color="textMuted" mt={4} h={5}>
                            {goalData.targetDate
                                ? getYearsOldStr({
                                      date: goalData.targetDate,
                                      userInfo,
                                  })
                                : undefined}
                        </Paragraph>
                    ),
                },
            ],
            isNextButtonDisabled: (goalData) =>
                !goalData.targetDate || isPastDate(goalData.targetDate),
        },
        {
            id: 'targetAmount',
            title: 'Choose a target vacation cost that you feel comfortable with.',
            fieldConfigs: [
                {
                    fieldName: 'targetAmount',
                    fieldType: 'currency',
                    min: 0,
                    slider: ({ goalData }) => {
                        return goalData.projectedAmount
                            ? {
                                  min: 0,
                                  max: goalData.projectedAmount * 2,
                                  step: 1_000_00, // $1,000
                              }
                            : {
                                  min: 0,
                                  max: 50_000_00, // $50,000
                                  step: 1_000_00, // $1,000
                              };
                    },
                    bottomContent: ({ goalData, showSlideup }) => {
                        const {
                            days,
                            people,
                            continent,
                            country,
                            expenseLevel,
                            projectedAmount,
                        } = goalData;
                        if (
                            !days ||
                            !people ||
                            !continent ||
                            !expenseLevel ||
                            !projectedAmount
                        )
                            return <></>;

                        const peopleStr = pluralize(
                            'person',
                            peopleInfoByValue[people].number,
                            true
                        );

                        return (
                            <>
                                <Paragraph
                                    fontSize="sm"
                                    color="textMuted"
                                    my={4}
                                >{`We estimate that a ${expenseLevelInfoByValue[
                                    expenseLevel
                                ].label.toLowerCase()} ${days} day trip for ${peopleStr} to ${
                                    !isEmpty(country) ? country : continent
                                } is approximately ${toDollars(
                                    projectedAmount
                                )}.`}</Paragraph>
                                <LinkText
                                    fontSize="sm"
                                    fontWeight="medium"
                                    onClick={() => {
                                        showSlideup('targetAmountAssumptions');
                                    }}
                                >
                                    See assumptions
                                </LinkText>
                            </>
                        );
                    },
                },
            ],
        },
    ],
    assumptions: [
        {
            id: 'targetAmountAssumptions',
            title: 'Assumptions',
            content: () => (
                <>
                    <Paragraph fontSize="sm" lineHeight="tall" mb={1}>
                        Your estimated vacation costs is calculated using:
                    </Paragraph>
                    <UnorderedList fontSize="sm" lineHeight="tall" spacing={1}>
                        <ListItem>
                            Average cost information from real travelers
                        </ListItem>
                        <ListItem>
                            Items included in cost estimates: airfare, lodgings,
                            local transportation, food, entertainment/activities
                        </ListItem>
                    </UnorderedList>
                    <Paragraph mt={4} fontSize="sm" lineHeight="tall">
                        Note: Everybody travels differently! This is our best
                        estimate to help you set a goal amount.
                    </Paragraph>
                </>
            ),
        },
    ],
    getGoalStatementContent: ({ goalData, onGoalStatementClick }) => {
        if (isEmpty(goalData.targetAmount) || isEmpty(goalData.targetDate)) {
            console.warn(
                'Failed to construct goal statement due to incomplete goalData.'
            );
            console.warn(goalData);
            return null;
        }

        const destinationStr = !isEmpty(goalData.country)
            ? goalData.country
            : goalData.continent;
        const peopleNumber = goalData.people
            ? peopleInfoByValue[goalData.people].number
            : undefined;
        const peopleStr = isNumber(peopleNumber)
            ? pluralize('person', peopleNumber, true)
            : undefined;
        const daysStr = goalData.days
            ? pluralize('day', goalData.days, true)
            : undefined;

        const iOrWe = getIOrWe(goalData);
        const color = VACATION.color;

        return (
            <>
                {`${capitalize(iOrWe)} want to have `}
                <GoalStatementLinkText
                    text={toDollars(goalData.targetAmount)}
                    onClick={() => onGoalStatementClick('targetAmount')}
                    color={color}
                />
                {' for a '}
                {destinationStr && (
                    <>
                        <GoalStatementLinkText
                            text={destinationStr}
                            onClick={() => onGoalStatementClick('continent')}
                            color={color}
                        />{' '}
                    </>
                )}
                {'vacation '}
                {peopleStr && (
                    <>
                        {'for '}
                        <GoalStatementLinkText
                            text={peopleStr}
                            onClick={() => onGoalStatementClick('people')}
                            color={color}
                        />{' '}
                    </>
                )}
                {daysStr && (
                    <>
                        {'for '}
                        <GoalStatementLinkText
                            text={daysStr}
                            onClick={() => onGoalStatementClick('days')}
                            color={color}
                        />{' '}
                    </>
                )}
                {'by '}
                <GoalStatementLinkText
                    text={dt(goalData.targetDate).format('MMMM, YYYY')}
                    onClick={() => onGoalStatementClick('yearMonth')}
                    color={color}
                />
                {'.'}
            </>
        );
    },
    getAssumptionsContent: () => {
        return (
            <>
                <Paragraph fontSize="sm" lineHeight="tall" mb={1}>
                    {'Your estimated vacation cost is calculated using:'}
                </Paragraph>
                <UnorderedList fontSize="sm" lineHeight="tall" spacing={1}>
                    <ListItem>{`Average cost information from real travelers`}</ListItem>
                    <ListItem>{`Items we’ve included: airfare, lodgings, local transportation, food, entertainment/activities`}</ListItem>
                </UnorderedList>
                <Paragraph mt={4} fontSize="sm" lineHeight="tall">
                    {`Note: Everybody travels differently! This is our best estimate to help you set a goal amount.`}
                </Paragraph>
            </>
        );
    },
    calculateDerivedGoalData: async ({
        oldGoalData,
        newGoalData,
    }): Promise<GoalData<VacationGoal>> => {
        const targetDate =
            oldGoalData.yearMonth !== newGoalData.yearMonth &&
            newGoalData.yearMonth?.length === 6
                ? getTargetDateFromYearMonth(newGoalData.yearMonth)
                : oldGoalData.targetDate;

        const projectedAmount = getProjectedAmount({
            people: newGoalData.people,
            days: newGoalData.days,
            continent: newGoalData.continent,
            country: newGoalData.country,
            expenseLevel: newGoalData.expenseLevel,
        });

        return {
            ...newGoalData,
            targetDate,
            projectedAmount,

            // update targetAmount if projected amount changed using formula above
            ...(!isEqual(oldGoalData.projectedAmount, projectedAmount) && {
                targetAmount: projectedAmount,
            }),
        };
    },
    getRecommendedRiskScore: (config) =>
        getRecommendedRiskScoreUsingTargetDate(config),
    getTargetDate: ({ goalData }) => goalData.targetDate,
    ...getGoalBaseConfig('VACATION'),
};

const getProjectedAmount = ({
    people,
    days,
    continent,
    country,
    expenseLevel,
}: {
    people?: VacationPeople;
    days?: number;
    continent?: VacationContinent;
    country?: VacationCountry;
    expenseLevel?: VacationExpenseLevel;
}): number | undefined => {
    if (
        isEmpty(people) ||
        isEmpty(days) ||
        isEmpty(continent) ||
        isEmpty(expenseLevel)
    ) {
        return undefined;
    }

    const numberOfPeople = peopleInfoByValue[people].number;
    const costPerDayPerPerson = !isEmpty(country)
        ? countryInfoByValue[country].costPerDayPerPersonByExpenseLevel[
              expenseLevel
          ]
        : continentInfoByValue[continent].costPerDayPerPersonByExpenseLevel[
              expenseLevel
          ];
    const flightCostPerPerson =
        longDistanceFlightCostPerPersonByExpenseLevel[expenseLevel];

    const projectedAmountDecimal =
        costPerDayPerPerson * numberOfPeople * days +
        flightCostPerPerson * numberOfPeople;

    const projectedAmount = getRoundedUp(projectedAmountDecimal, 250_00); // $250

    return projectedAmount;
};
